import { useMachine } from '@xstate/react'
import { useGlobalEvent } from 'beautiful-react-hooks'
import cx from 'classnames'
import { selectBorder } from 'common/Prose/Commands/table/selection'
import { LineMode } from 'common/Prose/utils/table'
import { BorderSelection } from 'common/Prose/utils/table/BorderSelection'
import { useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import { isDevelopment } from 'utils/nodeEnv'
import { borderMachine, BorderMachineContext } from './borderMachine'
import styles from './CellResizeHandler.module.scss'

export type CellResizeHandlerProps = {
  getPos: () => number
  columnBorders: Array<[number, number]>[]
  rowBorders: Array<[number, number]>[]
  columnWidths: number[]
  rowHeights: number[]
  handleResize: (type: LineMode, rowOrColIdx: number, newSizeOffset: number) => void
  handleSelectBorder: (...args: Parameters<typeof selectBorder>) => void
  selection?: BorderSelection
  tableRef: React.RefObject<HTMLTableElement>
  containerRef: React.RefObject<HTMLDivElement>
  setShowSnapBorder: (show: boolean) => void
}

export const CellResizeHandler: React.FC<React.PropsWithChildren<CellResizeHandlerProps>> = (props) => {
  const {
    getPos,
    columnWidths,
    rowHeights,
    columnBorders,
    rowBorders,
    handleResize,
    handleSelectBorder,
    selection,
    tableRef,
    containerRef,
    setShowSnapBorder,
  } = props
  const [machineState, sendEvent] = useMachine(borderMachine, {
    actions: { resize, selectFragment, selectBorder },
    devTools: isDevelopment(),
  })

  const machineRef = useRef<[typeof machineState, typeof sendEvent]>([machineState, sendEvent])

  useEffect(() => {
    machineRef.current = [machineState, sendEvent]
  }, [machineState, sendEvent])

  function resize(ctx: BorderMachineContext) {
    handleResize(ctx.lineMode, ctx.borderIdx - 1, ctx.offset)
  }

  function selectFragment(ctx: BorderMachineContext) {
    handleSelectBorder(ctx.lineMode, getPos(), ctx.borderIdx, ctx.fragmentIdx)
  }

  function selectBorder(ctx: BorderMachineContext) {
    handleSelectBorder(ctx.lineMode, getPos(), ctx.borderIdx)
  }

  const resizePreview = (e: MouseEvent) => {
    if (e.buttons === 1) {
      const {
        current: [currentState, sendEvent],
      } = machineRef
      const origin = currentState.context.origin
      const possibleOffset = currentState.context.lineMode === LineMode.Row ? e.clientY - origin : e.clientX - origin
      if (columnWidths.length === currentState.context.borderIdx) setShowSnapBorder(true)
      sendEvent({
        type: 'MouseMoveDown',
        offset: possibleOffset,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        tableSize: tableRef.current!.offsetWidth,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        containerSize: containerRef.current!.offsetWidth,
        columnWidths,
        originalEvent: e,
      })
    } else {
      sendEvent({ type: 'MouseMoveUp' })
      setShowSnapBorder(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousemove', resizePreview)
    return () => document.removeEventListener('mousemove', resizePreview)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnWidths, tableRef.current?.offsetWidth, containerRef.current?.offsetWidth])

  useGlobalEvent('dblclick', undefined, () => sendEvent({ type: 'DblClick' }))
  useGlobalEvent('mouseup', undefined, () => {
    sendEvent({ type: 'MouseUp' })
    setShowSnapBorder(false)
  })

  const columnResizeHandles = useMemo(() => {
    const { borderIdx, lineMode, offset, atLowerLimit } = machineState.context
    if (machineState.value !== 'resizing' || lineMode === LineMode.Row) return null
    return columnBorders[borderIdx].map(([columnFrom, columnTo]) => (
      <div
        key={`column-${columnFrom}-${columnTo}`}
        data-testid={`col-resize-${columnFrom}-${columnTo}`}
        className={cx(styles.columnResizeHandle, {
          [styles.atLowerLimit]: atLowerLimit,
        })}
        style={{
          gridColumn: `${(borderIdx + 1) * 2}`,
          gridRow: `${columnFrom * 2 + 3}/${(columnTo + 1) * 2 + 2}`,
          transform: `translateX(${offset - 1}px)`,
        }}
      />
    ))
  }, [columnBorders, machineState])

  const rowResizeHandles = useMemo(() => {
    const { borderIdx, lineMode, offset, atLowerLimit } = machineState.context
    if (machineState.value !== 'resizing' || lineMode === LineMode.Column) return null
    return rowBorders[borderIdx].map(([rowFrom, rowTo]) => (
      <div
        key={`row-${rowFrom}-${rowTo}`}
        data-testid={`row-resize-${rowFrom}-${rowTo}`}
        className={cx(styles.rowResizeHandle, {
          [styles.atLowerLimit]: atLowerLimit,
        })}
        style={{
          gridRow: `${(borderIdx + 1) * 2}`,
          gridColumn: `${rowFrom * 2 + 3}/${(rowTo + 1) * 2 + 2}`,
          transform: `translateY(${offset - 1}px)`,
        }}
      />
    ))
  }, [machineState, rowBorders])

  return (
    <>
      {columnBorders.map((cb, X) =>
        rowBorders.map((rb, Y) => {
          const columnStart = (X + 1) * 2
          const rowStart = (Y + 1) * 2
          const isColumnVisible = cb.filter(([start, end]) => Y >= start && Y <= end).length > 0
          const isRowVisible = rb.filter(([start, end]) => X >= start && X <= end).length > 0
          return (
            <React.Fragment key={`fragment-${X}-${Y}`}>
              {isColumnVisible && Y < rowBorders.length - 1 && (
                <div
                  data-testid={`col-${X}-${Y}`}
                  className={cx(styles.columnSelectionElement, {
                    [styles.first]:
                      columnBorders[X].some(([from, to]) => from === Y) ||
                      (selection instanceof BorderSelection &&
                        selection.verticals.every((vertical) => vertical.fragmentIdx !== undefined) &&
                        !selection.verticals.some(
                          (vertical) => vertical.rowOrColIdx === X && vertical.fragmentIdx === Y - 1,
                        )),
                    [styles.last]:
                      columnBorders[X].some(([from, to]) => to === Y) ||
                      (selection instanceof BorderSelection &&
                        selection.verticals.every((vertical) => vertical.fragmentIdx !== undefined) &&
                        !selection.verticals.some(
                          (vertical) => vertical.rowOrColIdx === X && vertical.fragmentIdx === Y + 1,
                        )),
                    [styles.soloFragment]:
                      selection instanceof BorderSelection &&
                      selection.verticals.find((selectedBorder) => selectedBorder.fragmentIdx !== undefined),
                    [styles.active]:
                      selection instanceof BorderSelection &&
                      selection.$anchor.pos === getPos() &&
                      selection.verticals.find(
                        (selectedBorder) =>
                          selectedBorder.rowOrColIdx === X &&
                          (selectedBorder.fragmentIdx === undefined || selectedBorder.fragmentIdx === Y),
                      ),
                  })}
                  style={{
                    gridColumn: `${columnStart}/${columnStart + 1}`,
                    gridRow: `${rowStart}/${rowStart + 2}`,
                  }}
                  onMouseDown={(e) => {
                    return sendEvent({
                      type: 'MouseDown',
                      borderIdx: X,
                      fragmentIdx: Y,
                      lineMode: LineMode.Column,
                      origin: e.clientX,
                      originalSize: columnWidths[X - 1],
                    })
                  }}
                />
              )}
              {isRowVisible && X < columnBorders.length - 1 && (
                <div
                  data-testid={`row-${X}-${Y}`}
                  className={cx(styles.rowSelectionElement, {
                    [styles.first]:
                      rowBorders[Y].some(([from, to]) => from === X) ||
                      (selection instanceof BorderSelection &&
                        selection.horizontals.every((horizontal) => horizontal.fragmentIdx !== undefined) &&
                        !selection.horizontals.some(
                          (horizontal) => horizontal.rowOrColIdx === Y && horizontal.fragmentIdx === X - 1,
                        )),
                    [styles.last]:
                      rowBorders[Y].some(([from, to]) => to === X) ||
                      (selection instanceof BorderSelection &&
                        selection.horizontals.every((horizontal) => horizontal.fragmentIdx !== undefined) &&
                        !selection.horizontals.some(
                          (horizontal) => horizontal.rowOrColIdx === Y && horizontal.fragmentIdx === X + 1,
                        )),
                    [styles.active]:
                      selection instanceof BorderSelection &&
                      selection.$anchor.pos === getPos() &&
                      selection.horizontals.find(
                        (selectedBorder) =>
                          selectedBorder.rowOrColIdx === Y &&
                          (selectedBorder.fragmentIdx === undefined || selectedBorder.fragmentIdx === X),
                      ),
                  })}
                  style={{
                    gridColumn: `${columnStart}/${columnStart + 2}`,
                    gridRow: `${rowStart}/${rowStart + 1}`,
                  }}
                  onMouseDown={(e) =>
                    sendEvent({
                      type: 'MouseDown',
                      borderIdx: Y,
                      fragmentIdx: X,
                      lineMode: LineMode.Row,
                      origin: e.clientY,
                      originalSize: rowHeights[Y - 1],
                    })
                  }
                />
              )}
            </React.Fragment>
          )
        }),
      )}
      {columnResizeHandles}
      {rowResizeHandles}
    </>
  )
}
